import type {
  EmployeeDocumentKind,
  EmployeeDocumentListResponse,
  EmployeeDocumentResponse,
} from '@kora/types';
import { apiRequest } from './client.js';

/**
 * Employee documents API. Tier 2 of the Mokoafrika HRIS-consolidation.
 * Wraps /v1/employees/:id/documents (multipart upload + list +
 * download + audit-logged delete).
 *
 * Routes live at apps/api/src/routes/employee-documents.ts. Storage
 * is pluggable behind DocumentStorage (LocalDiskStorage in dev,
 * R2/S3 in prod).
 */

export function listEmployeeDocuments(
  employeeId: string,
): Promise<EmployeeDocumentListResponse> {
  return apiRequest<EmployeeDocumentListResponse>(
    `/v1/employees/${encodeURIComponent(employeeId)}/documents`,
  );
}

export interface UploadEmployeeDocumentArgs {
  employeeId: string;
  kind: EmployeeDocumentKind;
  label: string;
  file: File;
}

/**
 * Multipart upload. Two parts:
 *   - "metadata" — JSON-stringified {kind, label}
 *   - "file"     — the binary
 *
 * The browser sets the multipart boundary itself when we pass
 * FormData; the API client skips the JSON Content-Type header in
 * that case (see api/client.ts).
 */
export function uploadEmployeeDocument(
  args: UploadEmployeeDocumentArgs,
): Promise<EmployeeDocumentResponse> {
  const fd = new FormData();
  fd.append('metadata', JSON.stringify({ kind: args.kind, label: args.label }));
  fd.append('file', args.file, args.file.name);
  return apiRequest<EmployeeDocumentResponse>(
    `/v1/employees/${encodeURIComponent(args.employeeId)}/documents`,
    { method: 'POST', body: fd },
  );
}

/**
 * Build a download URL the user can open in a new tab. The browser
 * handles the actual stream + Content-Disposition; the route is
 * authed so we can't pre-fetch and stash a blob URL.
 *
 * For programmatic download (e.g. saving a copy in JS), call
 * `fetch()` against this URL with the same auth header — there's no
 * way to pass auth via a plain link click without a temporary
 * download token, which is future work.
 */
export function buildDocumentDownloadUrl(employeeId: string, docId: string): string {
  return `/v1/employees/${encodeURIComponent(employeeId)}/documents/${encodeURIComponent(docId)}/download`;
}

export interface DeleteEmployeeDocumentArgs {
  employeeId: string;
  docId: string;
  reason: string;
}

export function deleteEmployeeDocument(
  args: DeleteEmployeeDocumentArgs,
): Promise<void> {
  return apiRequest<void>(
    `/v1/employees/${encodeURIComponent(args.employeeId)}/documents/${encodeURIComponent(args.docId)}`,
    { method: 'DELETE', body: { reason: args.reason } },
  );
}
